import { useEffect, useRef, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Sparkles,
  Upload,
  X,
} from 'lucide-react'
import Modal from '../Modal'
import Button from '../forms/Button'
import { planNetworkOptions, planTypes, type Plan } from '../../data/payorData'
import { usePayorMasterSelect, type PayorSelectOption } from './payorMasterOptions'

type AddPlanUploadModalProps = {
  open: boolean
  onClose: () => void
  onImport: (plans: Plan[]) => void
  /** When set, uploads the raw spreadsheet to the API instead of client-side import. */
  onUploadFile?: (file: File) => Promise<void>
}

type ParsedRow = {
  planName: string
  planId: string
  planType: string
  groupNumber: string
  network: string
  status: string
  rowNumber: number
  error?: string
}

const ACCEPTED_TYPES = ['.xlsx', '.xls', '.csv']
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024 // 10 MB

const HEADER_MAP: Record<string, keyof Omit<ParsedRow, 'rowNumber' | 'error'>> = {
  'plan name': 'planName',
  name: 'planName',
  planname: 'planName',
  'plan id': 'planId',
  planid: 'planId',
  id: 'planId',
  'plan type': 'planType',
  plantype: 'planType',
  type: 'planType',
  'group number': 'groupNumber',
  'group #': 'groupNumber',
  group: 'groupNumber',
  groupnumber: 'groupNumber',
  network: 'network',
  status: 'status',
}

function normalizeHeader(value: string): string {
  return value.toLowerCase().trim().replace(/\s+/g, ' ')
}

function normalizePlanType(raw: string, options: readonly PayorSelectOption[]): string {
  if (!raw) return ''
  const candidate = raw.trim()
  const match = options.find(
    (opt) => opt.value.toLowerCase() === candidate.toLowerCase()
  )
  if (match) return match.value
  const looseMatch = options.find(
    (opt) => opt.label.toLowerCase() === candidate.toLowerCase()
  )
  return looseMatch ? looseMatch.value : candidate
}

function normalizeNetwork(raw: string, options: readonly PayorSelectOption[]): string {
  if (!raw) return ''
  const match = options.find(
    (opt) => opt.value.toLowerCase() === raw.trim().toLowerCase()
  )
  return match ? match.value : raw.trim()
}

function normalizeStatus(raw: string): Plan['status'] {
  if (!raw) return 'active'
  const v = raw.trim().toLowerCase()
  if (v === 'active' || v === 'pending' || v === 'draft' || v === 'inactive') {
    return v
  }
  return 'active'
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

const AddPlanUploadModal = ({ open, onClose, onImport, onUploadFile }: AddPlanUploadModalProps) => {
  const { options: planTypeOptions } = usePayorMasterSelect('plan-types', planTypes)
  const { options: planNetworkSelectOptions } = usePayorMasterSelect('networks', planNetworkOptions)
  const [file, setFile] = useState<File | null>(null)
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [parseError, setParseError] = useState<string | null>(null)
  const [isParsing, setIsParsing] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setFile(null)
      setRows([])
      setParseError(null)
      setIsParsing(false)
      setIsUploading(false)
      setIsDragging(false)
    }
  }, [open])

  const parseFile = async (incoming: File) => {
    setIsParsing(true)
    setParseError(null)
    setRows([])
    try {
      const XLSX = await import('xlsx')
      const buffer = await incoming.arrayBuffer()
      const workbook = XLSX.read(buffer, { type: 'array' })
      const sheetName = workbook.SheetNames[0]
      if (!sheetName) {
        setParseError('The file appears to be empty.')
        return
      }
      const sheet = workbook.Sheets[sheetName]
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
        defval: '',
        raw: false,
      })
      if (raw.length === 0) {
        setParseError('No rows found in the spreadsheet.')
        return
      }

      const headerKeys = Object.keys(raw[0])
      const headerLookup = new Map<keyof Omit<ParsedRow, 'rowNumber' | 'error'>, string>()
      for (const key of headerKeys) {
        const normalized = normalizeHeader(key)
        const target = HEADER_MAP[normalized]
        if (target && !headerLookup.has(target)) {
          headerLookup.set(target, key)
        }
      }

      if (!headerLookup.has('planName')) {
        setParseError(
          'Could not find a "Plan name" column. Make sure your first row has headers like Plan Name, Plan ID, Plan Type.'
        )
        return
      }

      const parsed: ParsedRow[] = raw.map((row, index) => {
        const planName = String(row[headerLookup.get('planName') ?? ''] ?? '').trim()
        const planId = String(row[headerLookup.get('planId') ?? ''] ?? '').trim()
        const planType = normalizePlanType(
          String(row[headerLookup.get('planType') ?? ''] ?? ''),
          planTypeOptions
        )
        const groupNumber = String(row[headerLookup.get('groupNumber') ?? ''] ?? '').trim()
        const network = normalizeNetwork(
          String(row[headerLookup.get('network') ?? ''] ?? ''),
          planNetworkSelectOptions
        )
        const status = normalizeStatus(
          String(row[headerLookup.get('status') ?? ''] ?? '')
        )
        const error = !planName ? 'Missing plan name' : undefined
        return {
          planName,
          planId,
          planType,
          groupNumber,
          network,
          status,
          rowNumber: index + 2,
          error,
        }
      })
      setRows(parsed)
    } catch (err) {
      console.error('Failed to parse spreadsheet:', err)
      setParseError('We couldn\'t read that file. Please upload a valid .xlsx, .xls, or .csv file.')
    } finally {
      setIsParsing(false)
    }
  }

  const handleFiles = (incoming: FileList | File[]) => {
    setParseError(null)
    const candidate = Array.from(incoming)[0]
    if (!candidate) return
    const ext = `.${candidate.name.split('.').pop()?.toLowerCase() ?? ''}`
    if (!ACCEPTED_TYPES.includes(ext)) {
      setParseError(`Unsupported file type. Please upload an Excel (.xlsx, .xls) or CSV file.`)
      return
    }
    if (candidate.size > MAX_UPLOAD_BYTES) {
      setParseError(`File is larger than 10 MB.`)
      return
    }
    setFile(candidate)
    if (onUploadFile) return
    void parseFile(candidate)
  }

  const handleDownloadTemplate = async () => {
    const XLSX = await import('xlsx')
    const data = [
      ['Plan name', 'Plan ID', 'Plan type', 'Group number', 'Network', 'Status'],
      ['Premium PPO 2025', 'PPO-2025-001', 'PPO', 'GRP-12345', 'In-network', 'active'],
      ['Choice HMO', 'HMO-2025-002', 'HMO', 'GRP-67890', 'Preferred', 'pending'],
    ]
    const sheet = XLSX.utils.aoa_to_sheet(data)
    sheet['!cols'] = [
      { wch: 24 },
      { wch: 18 },
      { wch: 12 },
      { wch: 16 },
      { wch: 16 },
      { wch: 10 },
    ]
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, sheet, 'Plans')
    XLSX.writeFile(workbook, 'plan-import-template.xlsx')
  }

  const validRows = rows.filter((row) => !row.error)
  const skippedRows = rows.filter((row) => row.error)

  const handleImport = async () => {
    if (onUploadFile && file) {
      setIsUploading(true)
      try {
        await onUploadFile(file)
        onClose()
      } catch {
        // Parent shows toast; keep modal open for retry.
      } finally {
        setIsUploading(false)
      }
      return
    }

    if (validRows.length === 0) return
    const now = Date.now()
    const plans: Plan[] = validRows.map((row, index) => ({
      id: `plan-${now}-${index}`,
      planName: row.planName,
      planId: row.planId || undefined,
      planType: row.planType,
      groupNumber: row.groupNumber || undefined,
      network: row.network || undefined,
      autoCheckFrequency: '',
      status: row.status,
      createdAt: new Date().toISOString().slice(0, 10),
    }))
    onImport(plans)
  }

  const previewRows = rows.slice(0, 6)

  return (
    <Modal isOpen={open} onClose={onClose} title="Upload plans from Excel" size="xl">
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-600 max-w-2xl">
            Upload a spreadsheet with one plan per row. We support .xlsx, .xls, and .csv. Use the
            template below to ensure column headers are detected automatically.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDownloadTemplate}
            className="inline-flex items-center gap-1.5 shrink-0"
          >
            <Download size={14} />
            Download template
          </Button>
        </div>

        {!file && (
          <div
            onDragOver={(e) => {
              e.preventDefault()
              setIsDragging(true)
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setIsDragging(false)
              if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files)
            }}
            className={`rounded-xl border-2 border-dashed transition-colors p-6 sm:p-8 text-center ${
              isDragging
                ? 'border-primary-500 bg-primary-50/60'
                : 'border-gray-300 bg-gray-50/60 hover:bg-gray-50'
            }`}
          >
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white border border-gray-200 text-primary-600">
              <Upload size={20} />
            </div>
            <p className="mt-3 text-sm font-medium text-gray-900">
              Drag and drop your spreadsheet here
            </p>
            <p className="mt-1 text-xs text-gray-500">
              .xlsx, .xls, or .csv • Up to 10 MB
            </p>
            <div className="mt-4">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept={ACCEPTED_TYPES.join(',')}
                onChange={(e) => {
                  if (e.target.files?.length) handleFiles(e.target.files)
                  e.target.value = ''
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                Browse files
              </Button>
            </div>
          </div>
        )}

        {file && (
          <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-green-50 text-green-600">
              <FileSpreadsheet size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
              <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setFile(null)
                setRows([])
                setParseError(null)
              }}
              className="text-gray-400 hover:text-gray-700 p-1"
              aria-label="Remove file"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {isParsing && (
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
            <Loader2 size={16} className="animate-spin text-primary-600" />
            Parsing spreadsheet…
          </div>
        )}

        {parseError && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{parseError}</span>
          </div>
        )}

        {rows.length > 0 && !isParsing && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-semibold text-green-700">
                <CheckCircle2 size={12} />
                {validRows.length} ready to import
              </span>
              {skippedRows.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                  <AlertTriangle size={12} />
                  {skippedRows.length} skipped (missing plan name)
                </span>
              )}
              <span className="text-xs text-gray-500">
                Showing first {Math.min(previewRows.length, rows.length)} of {rows.length} rows
              </span>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-[760px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 w-12">
                      Row
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Plan name
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Plan ID
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Type
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Group #
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Network
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {previewRows.map((row) => (
                    <tr
                      key={row.rowNumber}
                      className={row.error ? 'bg-amber-50/40' : ''}
                    >
                      <td className="px-3 py-2 text-xs text-gray-500">{row.rowNumber}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        {row.planName || (
                          <span className="text-amber-600">— missing —</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">{row.planId || '—'}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">{row.planType || '—'}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {row.groupNumber || '—'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">{row.network || '—'}</td>
                      <td className="px-3 py-2 text-sm text-gray-700 capitalize">
                        {row.status || 'active'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500 inline-flex items-center gap-1.5">
            <Sparkles size={12} className="text-primary-500" />
            You can edit any imported plan before saving the payor.
          </p>
          <div className="flex items-center gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleImport()}
              disabled={
                onUploadFile
                  ? !file || isUploading
                  : validRows.length === 0 || isParsing
              }
            >
              {onUploadFile
                ? isUploading
                  ? 'Uploading…'
                  : 'Upload plans'
                : validRows.length > 0
                  ? `Import ${validRows.length} plan${validRows.length > 1 ? 's' : ''}`
                  : 'Import plans'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default AddPlanUploadModal
